import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, CreditCard, Tag, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Field } from "../components/ui-kit";
import { api, resolveImg } from "../lib/api";
import { useCart } from "../lib/cart";
import { useMember } from "../lib/memberAuth";

export default function Checkout() {
  const { items, clear } = useCart();
  const { member } = useMember();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  useEffect(() => {
    if (member) setForm((f) => ({ name: f.name || member.preferred_name || member.name || "", email: f.email || member.email || "", phone: f.phone || member.phone || "" }));
  }, [member]);
  const [promo, setPromo] = useState("");
  const [appliedPromo, setAppliedPromo] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);

  const cartPayload = items.map((i) => ({ type: i.type, id: i.id, tier_id: i.tier_id || null, qty: i.qty, size: i.size || "", color: i.color || "" }));

  const fetchQuote = useCallback(async (code, email) => {
    if (items.length === 0) { setQuote(null); return; }
    try {
      const { data } = await api.post("/checkout/quote", { cart: cartPayload, promo_code: code || "", email: email || "" });
      setQuote(data);
      setQuoteErr("");
      if (code && data.promo_error) toast.error(data.promo_error);
      else if (code && data.promo_code) toast.success(`Code ${data.promo_code} applied!`);
    } catch (e) {
      setQuote(null);
      setQuoteErr(e?.response?.data?.detail || "Unable to price your order");
    }
    // eslint-disable-next-line
  }, [JSON.stringify(cartPayload)]);

  useEffect(() => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchQuote(appliedPromo, form.email), 250);
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [fetchQuote, appliedPromo]);

  const pay = async (method) => {
    if (!form.name || !form.email) { toast.error("Please enter your name and email."); return; }
    setBusy(method);
    try {
      const { data } = await api.post("/checkout/create", {
        cart: cartPayload, promo_code: appliedPromo, name: form.name, email: form.email,
        phone: form.phone, method, origin_url: window.location.origin,
      });
      if (data.free) { clear(); navigate(`/order/success?order_id=${data.order_id}`); return; }
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unable to start checkout. Please try again.");
      setBusy(false);
    }
  };

  const row = (label, val, opts = {}) => (
    <div className={`flex justify-between text-sm ${opts.bold ? "font-bold text-base pt-3 border-t border-black/10" : ""}`} data-testid={opts.testid}>
      <span className={opts.bold ? "" : "text-black/60"}>{label}</span>
      <span className={opts.red ? "text-[#E63946] font-medium" : ""}>{val}</span>
    </div>
  );

  return (
    <div data-testid="checkout-page" className="pt-[72px] min-h-screen bg-[#FAFAFA]">
      <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-14">
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl mb-8">Checkout</h1>
        {items.length === 0 ? (
          <div className="py-20 text-center text-black/50">
            <ShoppingBag className="mx-auto mb-4 text-[#E63946]" size={36} />
            <p className="mb-4">Your cart is empty.</p>
            <Link to="/store" className="inline-block rounded-full bg-[#E63946] text-white px-7 py-3 text-sm font-medium hover:bg-[#BA1B25]">Visit the Store</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_420px] gap-8 items-start">
            <div className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 space-y-5">
              <h2 className="font-display font-bold text-lg">Your Information</h2>
              <Field label="Full Name" name="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Field label="Email" name="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Field label="Phone" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <p className="text-xs text-black/40">No account needed — you can check out as a guest. Your receipt and any tickets will be emailed to you.</p>
            </div>

            <div className="bg-white border border-black/10 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="font-display font-bold text-lg">Order Summary</h2>
              <div className="space-y-3">
                {items.map((i) => (
                  <div key={`${i.id}${i.size}${i.color}${i.tier_id || ""}`} className="flex gap-3 items-center">
                    <div className="w-12 h-12 rounded-md bg-[#F4F4F5] overflow-hidden shrink-0">
                      {i.image && <img src={resolveImg(i.image)} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{i.name}</p>
                      <p className="text-xs text-black/45">{[i.size, i.color].filter(Boolean).join(" / ")} × {i.qty}</p>
                    </div>
                    <span className="text-sm font-medium">${(i.price * i.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2 pt-2">
                <div className="relative flex-1">
                  <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/30" />
                  <input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="Promo code" data-testid="promo-input"
                    className="w-full border border-black/15 rounded-full pl-8 pr-3 py-2.5 text-sm uppercase focus:border-[#E63946] outline-none" />
                </div>
                <button onClick={() => setAppliedPromo(promo)} data-testid="promo-apply-btn"
                  className="rounded-full border border-black/15 px-5 py-2.5 text-sm hover:bg-[#0A0A0A] hover:text-white transition-colors">Apply</button>
              </div>

              {quoteErr && <p className="text-sm text-[#E63946]">{quoteErr}</p>}
              {quote && (
                <div className="space-y-2 pt-2" data-testid="order-breakdown">
                  {row("Subtotal", `$${quote.subtotal.toFixed(2)}`, { testid: "bd-subtotal" })}
                  {quote.discount > 0 && row(`Discount${quote.promo_code ? ` (${quote.promo_code})` : ""}`, `-$${quote.discount.toFixed(2)}`, { red: true, testid: "bd-discount" })}
                  {quote.service_fee > 0 && row(quote.service_fee_label || "Service Fee", `$${quote.service_fee.toFixed(2)}`, { testid: "bd-fee" })}
                  {quote.shipping > 0 && row("Shipping", `$${quote.shipping.toFixed(2)}`, { testid: "bd-shipping" })}
                  {quote.tax > 0 && row(`Sales Tax (${quote.tax_rate}%)`, `$${quote.tax.toFixed(2)}`, { testid: "bd-tax" })}
                  {row("Total", `$${quote.total.toFixed(2)}`, { bold: true, testid: "bd-total" })}
                  {quote.service_fee > 0 && <p className="text-[11px] text-black/40">The {quote.service_fee_label?.toLowerCase() || "service fee"} helps cover payment processing costs.</p>}
                </div>
              )}

              <div className="space-y-3 pt-2">
                {quote?.free ? (
                  <button onClick={() => pay("free")} disabled={!!busy} data-testid="pay-free-btn"
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-4 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
                    {busy ? <Loader2 className="animate-spin" size={16} /> : "Complete Free Order"}
                  </button>
                ) : (
                  <>
                    <button onClick={() => pay("stripe")} disabled={!!busy || !quote} data-testid="pay-stripe-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63946] text-white py-4 font-medium hover:bg-[#BA1B25] transition-colors disabled:opacity-60">
                      {busy === "stripe" ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : <><CreditCard size={17} /> Pay with Card</>}
                    </button>
                    <button onClick={() => pay("paypal")} disabled={!!busy || !quote} data-testid="pay-paypal-btn"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#003087] text-white py-4 font-medium hover:bg-[#001c4d] transition-colors disabled:opacity-60">
                      {busy === "paypal" ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : <>Checkout with <span className="font-bold italic">PayPal</span></>}
                    </button>
                  </>
                )}
                <p className="text-center text-xs text-black/40">You will never be charged more than the total shown above.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
